export const customerPageData = {
  stats: [
    { value: "35%", label: "Better Retention" },
    { value: "45%", label: "More Repeat Revenue" },
    { value: "360°", label: "Customer View" },
    { value: "99%", label: "Account Accuracy" },
  ],
  statsAccent: "from-rose-600 to-pink-600",

  challengeTitle: "Managing Customers Manually Means Losing Customers",
  challengeSubtitle:
    "As your customer base grows, manual management creates blind spots that cost you revenue and loyalty.",
  challenges: [
    {
      icon: "👁️",
      title: "No Single Customer View",
      description:
        "Order history, payment status, and communications are scattered across systems — creating dangerous blind spots.",
    },
    {
      icon: "📉",
      title: "Undetected Churn Risk",
      description:
        "Without predictive analytics, at-risk customers are invisible until it's too late and they've already left.",
    },
    {
      icon: "💼",
      title: "Missed Upsell Opportunities",
      description:
        "Sales teams lack buying-pattern data to identify and act on upsell or cross-sell opportunities in real time.",
    },
    {
      icon: "⏳",
      title: "Hours Wasted on Data Entry",
      description:
        "Manual tracking of orders, payments, and interactions steals time from relationship-building and selling.",
    },
    {
      icon: "🔔",
      title: "Reactive Collections Only",
      description:
        "Without real-time payment dashboards, finance teams only discover overdue accounts long after they're overdue.",
    },
    {
      icon: "📊",
      title: "No Segment-Level Strategy",
      description:
        "Treating all customers the same ignores the massive revenue potential hidden in your highest-value segments.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-rose-50 text-rose-700 border-rose-100",
    solutionIconBg: "bg-rose-50",
    solutionIconColor: "text-rose-600",
    solutionAccent: "from-rose-600 to-pink-600",
  },

  solutionTitle: "A Complete 360° AI-Powered Customer Intelligence Platform",
  solutionSubtitle:
    "Know every customer inside out. Predict their next move. Keep them coming back.",
  solutions: [
    {
      icon: "🗂️",
      title: "360° Customer Profiles",
      description:
        "Every order, payment, interaction, and preference in one complete customer record — always accurate, always live.",
    },
    {
      icon: "🤖",
      title: "AI Churn Prediction",
      description:
        "Machine learning identifies customers at risk of leaving weeks before they disengage — enabling proactive retention.",
    },
    {
      icon: "🎯",
      title: "Smart Customer Segmentation",
      description:
        "Automatically segment customers by spend, frequency, location, and loyalty tier for targeted strategies.",
    },
    {
      icon: "💳",
      title: "Real-Time Payment Tracking",
      description:
        "Monitor payment statuses, outstanding balances, and credit utilisation for every account in real time.",
    },
    {
      icon: "📣",
      title: "Personalised Engagement Automation",
      description:
        "AI triggers personalised offers, loyalty rewards, and re-engagement campaigns at exactly the right moment.",
    },
    {
      icon: "📈",
      title: "Upsell & Cross-Sell Intelligence",
      description:
        "Buying-pattern analysis surfaces upsell and cross-sell opportunities — turning existing customers into bigger revenue.",
    },
    {
      icon: "🏆",
      title: "Loyalty & Reward Management",
      description:
        "Build and manage tiered loyalty programmes that increase repeat purchase frequency and average order value.",
    },
    {
      icon: "📞",
      title: "Communication Hub",
      description:
        "All customer communications — calls, messages, emails, and order updates — logged and accessible in one place.",
    },
    {
      icon: "📊",
      title: "Revenue Analytics",
      description:
        "Deep customer revenue analytics show your most profitable segments, fastest-growing accounts, and hidden opportunities.",
    },
  ],

  howItWorksTitle: "From Customer Data to Revenue Growth in 4 Steps",
  howItWorksSteps: [
    {
      icon: "🔗",
      title: "Unify Customer Data",
      description:
        "Connect your order management, payments, and CRM data to build complete customer profiles automatically.",
    },
    {
      icon: "🤖",
      title: "AI Analyses Behaviour",
      description:
        "The AI engine continuously analyses buying patterns, payment behaviour, and engagement signals.",
    },
    {
      icon: "🎯",
      title: "Segment & Personalise",
      description:
        "Customers are automatically segmented and personalised campaigns are triggered to maximise retention.",
    },
    {
      icon: "📈",
      title: "Grow Retention & Revenue",
      description:
        "Monitor retention rates, repeat revenue, and customer lifetime value improving in real time.",
    },
  ],

  outcomeTitle: "Retention, Revenue, and Relationships — All Improved",
  outcomeSubtitle: "What businesses achieve with EkkBaz AI customer management.",
  outcomes: [
    {
      value: "35%",
      metric: "Higher Retention Rate",
      description:
        "AI churn prediction and personalised retention campaigns reduce customer attrition by 35% within the first quarter.",
    },
    {
      value: "45%",
      metric: "More Repeat Revenue",
      description:
        "Smart upsell and cross-sell recommendations drive a 45% increase in revenue from existing customers.",
    },
    {
      value: "3×",
      metric: "Faster Collections",
      description:
        "Real-time payment dashboards and automated reminders reduce overdue account rates by 60% and accelerate collections 3×.",
    },
  ],
  outcomeImage: "/assets/images/products/customer2.png",

  ctaTitle: "Turn Your Customer Base into Your Biggest Growth Engine",
  ctaSubtitle:
    "Book a consultation and discover how EkkBaz AI can increase your retention by 35% and grow repeat revenue by 45% — starting today.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const customerData = [
  {
    id: 1,
    extraLabel: "The Challenge",
    userType: "Managing Thousands of Customers Manually Is Unsustainable",
    description:
      "As your customer base grows, manually tracking payment statuses, order histories, and account health becomes impossible. Teams lose visibility into at-risk customers, miss upsell opportunities, and waste hours on data entry — leading to churn, revenue loss, and operational chaos.",
    banner: "/assets/images/products/customer1.png",
  },
  {
    id: 2,
    extraLabel: "Our Solution",
    userType: "A Complete AI-Powered Customer Management Platform",
    description:
      "EkkBaz AI gives you a 360° view of every customer — their full order history, payment status, outstanding balances, and engagement level — in one intelligent dashboard. Segment customers by loyalty, location, and spend. Track every interaction and payment in real time. Never lose track of a single account, no matter how fast you scale.",
    banner: "/assets/images/products/customer2.png",
  },
  {
    id: 3,
    extraLabel: "The Outcome",
    userType: "Increase Retention, Reduce Churn, and Drive Repeat Revenue with AI",
    description:
      "EkkBaz AI's customer intelligence engine analyzes buying patterns to predict churn, identify upsell opportunities, and personalize engagement strategies — helping businesses increase customer retention by 35% and grow repeat order revenue by 45%.",
    banner: "/assets/images/products/customer3.png",
  },
];

export const customerFAQs = [
  {
    question: "What customer data does EkkBaz AI track?",
    answer:
      "EkkBaz AI tracks complete order histories, payment statuses, outstanding balances, delivery records, communication logs, and behavioral patterns for every customer — giving you a full 360° view of each account.",
  },
  {
    question: "How does EkkBaz AI help reduce customer churn?",
    answer:
      "EkkBaz AI's predictive analytics identify customers showing early signs of disengagement and automatically trigger personalized retention actions — such as targeted offers or proactive account manager outreach — before churn occurs.",
  },
  {
    question: "Can EkkBaz AI segment customers automatically?",
    answer:
      "Yes. EkkBaz AI automatically segments customers based on purchase frequency, spend volume, payment behavior, and geographic proximity, enabling highly targeted marketing and sales strategies for each segment.",
  },
];
